import React, { useMemo, useState } from 'react';
import { allVisibleSelected, cityCounts, filterTours, setVisible, toggleId } from './helpers';

/**
 * The searchable tour list shared by the standalone picker (TourPicker) and
 * the onboarding Tours step. Built for companies with hundreds of tours
 * (Walks): search by any words in the title or city, narrow to one city when
 * the company runs tours in several, select or clear everything shown in one
 * tap, and review picks with "Show selected only". Native checkboxes rather
 * than MUI ones keep a 500-row list cheap to render.
 *
 * Controlled: the parent owns `selected` (a Set of tour ids).
 */
const TourChecklist = ({ tours, selected, onChange, idPrefix = 'tour-picker' }) => {
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);

  const cities = useMemo(() => cityCounts(tours), [tours]);
  const showCities = cities.length > 1;

  const visible = useMemo(() => {
    const inCity = city ? tours.filter(tour => `${tour.city || ''}`.toLowerCase() === city) : tours;
    const matching = filterTours(inCity, query);
    return selectedOnly ? matching.filter(tour => selected.has(tour._id)) : matching;
  }, [tours, city, query, selectedOnly, selected]);

  const everyVisibleSelected = allVisibleSelected(selected, visible);
  const narrowed = Boolean(query || city);

  const chipClass = active =>
    `shrink-0 h-9 px-3.5 rounded-full border border-solid text-sm cursor-pointer transition-colors whitespace-nowrap ${
      active
        ? 'bg-slate-900 border-slate-900 text-white'
        : 'bg-white border-slate-300 text-slate-700 hover:border-slate-500'
    }`;

  return (
    <div>
      <label
        htmlFor={`${idPrefix}-search`}
        className="block text-sm font-semibold text-slate-700 mb-2">
        Search {tours.length} tours
      </label>
      <input
        id={`${idPrefix}-search`}
        type="search"
        autoComplete="off"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Tour name or city"
        className="w-full box-border h-12 px-4 rounded-xl border border-solid border-slate-300 bg-white text-base text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
      />

      {showCities && (
        <div
          className="flex gap-2 overflow-x-auto mt-3 pb-1 -mx-1 px-1"
          role="group"
          aria-label="Filter by city">
          <button
            type="button"
            aria-pressed={city === ''}
            className={chipClass(city === '')}
            onClick={() => setCity('')}>
            All cities
          </button>
          {cities.map(({ key, label, count }) => (
            <button
              key={key}
              type="button"
              aria-pressed={city === key}
              className={chipClass(city === key)}
              onClick={() => setCity(city === key ? '' : key)}>
              {label} <span className={city === key ? 'text-slate-300' : 'text-slate-500'}>{count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-4 mb-2 flex-wrap">
        <button
          type="button"
          disabled={visible.length === 0}
          onClick={() => onChange(setVisible(selected, visible, !everyVisibleSelected))}
          className="bg-transparent border-0 p-0 text-sm font-semibold text-slate-900 underline underline-offset-2 cursor-pointer disabled:text-slate-400 disabled:cursor-default">
          {everyVisibleSelected
            ? `Clear ${visible.length} shown`
            : narrowed
            ? `Select all ${visible.length} matching`
            : `Select all ${visible.length}`}
        </button>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={selectedOnly}
            onChange={e => setSelectedOnly(e.target.checked)}
            className="w-4 h-4 m-0 accent-indigo-600"
          />
          Show selected only
        </label>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-slate-600 py-10 text-center m-0" data-testid="tour-picker-empty">
          {selectedOnly && !narrowed
            ? 'No tours selected yet.'
            : query
            ? `No tours match "${query}".`
            : 'No tours in this city.'}
        </p>
      ) : (
        <ul className="list-none m-0 p-0 bg-white rounded-xl border border-solid border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {visible.map(tour => {
            const checked = selected.has(tour._id);
            return (
              <li key={tour._id}>
                <label
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer min-h-[52px] ${
                    checked ? 'bg-indigo-50' : 'hover:bg-slate-50'
                  }`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(toggleId(selected, tour._id))}
                    className="w-5 h-5 m-0 shrink-0 accent-indigo-600"
                  />
                  <span className="flex flex-col min-w-0">
                    <span className="text-[15px] text-slate-900 break-words">{tour.title}</span>
                    {showCities && tour.city && (
                      <span className="text-xs text-slate-500 capitalize mt-0.5">{tour.city}</span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TourChecklist;
